import React, { useState, useEffect } from 'react';
import { usePaymentForm } from 'customHook/usePaymentForm';
import { useDispatch, useSelector } from 'react-redux';
import { setPaymentInfo, cleanPaymentInfo } from 'redux/actions'
import Input from 'components/Input';
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import FormContainer from 'components/FormContainer';
import InputMask from 'react-input-mask';
import {
  creditCardMask,
  expiryDateMask,
  cvvMask,
} from './inputMasks'
import { detectCardType } from 'utils/detectCardType';
import { usePlacesWidget } from 'react-google-autocomplete'
import { Row } from './styles';
import checkMark from 'assets/svg/checkMark.svg'

const PaymentMethod = ({ creditCardType, setCreditCardType }) => {

  const [coverInput, setCoverInput] = useState('')
  const dispatch = useDispatch()
  const paymentInfo = useSelector(state => state.paymentInfoReducer.paymentInfo)
  const { paymentError, setPaymentError } = usePaymentForm(creditCardType)

  useEffect(()=> {
    return () => {
      dispatch(cleanPaymentInfo())
      setCoverInput('')
      creditCardMask.mask = '9999 9999 9999 9999'
    }    
  },[dispatch])

  const { ref } = usePlacesWidget({
    apiKey: `${process.env.REACT_APP_GOOGLE_APIKEY}&loading=async`,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];

      const getComponent = (type) =>
        components.find((component) => component.types.includes(type))?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      const streetAddressLine2 = ""; // Google Autocomplete doesn't return this
      const city = getComponent("locality") || getComponent("sublocality") || "";
      const province = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");
      dispatch(setPaymentInfo({
        streetAddress,
        streetAddressLine2,
        city,
        province,
        postalCode,
        country
      })) 
    },
    options: {
      types: ['address'],
    },
  }) 

  const onChangeCreditCard = (e) => {
    
    setCoverInput(e.target.value)
    dispatch(setPaymentInfo({creditCardNumber: e.target.value}))
    if(e.target.value.length < 19) {
      setCreditCardType('')
      setPaymentError((paymentError) => ({...paymentError, creditCardNumberError: false}))
    } else {
      const type = detectCardType(e.target.value.replace(/\s/g, ''))
      if(type.length > 0) {
        setPaymentError((paymentError) => ({...paymentError, creditCardNumberError: false}))
      } else {
        setPaymentError((paymentError) => ({...paymentError, creditCardNumberError: true}))
      }
      setCreditCardType(type)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="cardTitle">BILLING INFORMATION</div>
      </CardHeader>
      <FormContainer>
        <Row>
          <Input
            mainPage
            required
            title="CARDHOLDER NAME"
            value={paymentInfo.cardHolderName}
            error={paymentError.cardHolderNameError}
            onChange={(e) => {
              dispatch(setPaymentInfo({cardHolderName: e.target.value}))
              if(paymentError.cardHolderNameError) {
                setPaymentError((paymentError)=>({...paymentError, cardHolderNameError: false}))
              }
            }}
            additionalstyle={`
              width: calc(50% - 8px);
            `}
          />
        </Row>
        <Row>
          <InputMask
            mask={creditCardMask.mask}
            maskPlaceholder=''
            value={coverInput}
            error={paymentError.creditCardNumberError}
            onChange={onChangeCreditCard}
          >
            <Input
              mainPage
              required
              title="CREDIT CARD"
              maxLength={19}
              additionalstyle={`
                width: 50%;
                input{
                  ${
                    creditCardType? `
                      background-image: url(${checkMark});
                      background-repeat: no-repeat;
                      background-position: 80% 50%;
                    `: null
                  }
                }
              `}
            />
          </InputMask>
          <InputMask
            mask={expiryDateMask.mask}
            maskPlaceholder=''
            value={paymentInfo.cardExpirationDate}
            error={paymentError.cardExpirationDateError}
            onChange={(e)=> {
              dispatch(setPaymentInfo({cardExpirationDate: e.target.value}))
              if(paymentError.cardExpirationDateError) {
                setPaymentError((paymentError)=>({...paymentError, cardExpirationDateError: false}))
              }
            }}
          >
            <Input
              mainPage
              required
              title="EXPIRY"
              additionalstyle={`
                width: calc(25% - 8px);
              `}
            />
          </InputMask>
          <InputMask
            mask={cvvMask.mask}
            maskPlaceholder=''
            value={paymentInfo.cardCVV}
            error={paymentError.cardCVVError}
            onChange={(e) => {
              dispatch(setPaymentInfo({cardCVV: e.target.value}))
              if(paymentError.cardCVVError) {
                setPaymentError((paymentError)=>({...paymentError, cardCVVError: false}))
              }
            }}
          >
            <Input
              mainPage
              required
              title="CVV"
              additionalstyle={`
                width: calc(25% - 8px);
              `}
            />
          </InputMask>
        </Row>
        <Row>
          <div className="address">
            <Input
              mainPage
              required
              title="STREET ADDRESS"
              inputFieldRef={ref}
              value={paymentInfo.streetAddress}
              error={paymentError.streetAddressError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    streetAddress: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="address">
            <Input
              mainPage
              title="STREET ADDRESS LINE 2"
              value={paymentInfo.streetAddressLine2}
              error={paymentError.streetAddressLine2Error}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    streetAddressLine2: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
        </Row>
        <Row>
          <div className="city">
            <Input
              mainPage
              title="CITY"
              value={paymentInfo.city}
              error={paymentError.cityError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    city: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="province">
            <Input
              mainPage
              title="PROVINCE / STATE"
              value={paymentInfo.province}
              error={paymentError.provinceError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    province: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="zipCode">
            <Input
              mainPage
              title="POSTAL / ZIP CODE"
              value={paymentInfo.postalCode}
              error={paymentError.postalCodeError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    postalCode: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
          <div className="country">
            <Input
              mainPage
              title="COUNTRY"
              value={paymentInfo.country}
              error={paymentError.countryError}
              onChange={(e) => {
                dispatch(
                  setPaymentInfo({
                    country: e.target.value,
                  })
                );
              }}
              additionalstyle={`
                width: 100%;
              `}
            />
          </div>
        </Row>
      </FormContainer>
    </Card>
  )
}

export default PaymentMethod;

